// Client for Giga's public integration catalog — the single source of truth.
//
// Base URL is swappable per environment via VITE_GIGA_API_URL:
//   - local/VM testing → https://vm.getgiga.com (default below)
//   - production        → set VITE_GIGA_API_URL=https://app.getgiga.com
// The endpoints are anonymous + CORS-open (public marketing data), so no token.

// NOTE: `import.meta.env` is a Vite BUILD-TIME construct. In Vite it's statically
// inlined, but in runtimes that don't provide it (e.g. the Magic Patterns preview)
// `import.meta.env` is `undefined`, and `undefined.VITE_GIGA_API_URL` throws
// "Cannot read properties of undefined". Guard both `import.meta` and `.env` so it
// degrades to the default instead of crashing. IMPORTANT: this same guard must
// also exist in the Magic Patterns project — MP re-exports overwrite this file, so
// a fix only in the repo gets clobbered on the next MP sync.
export const GIGA_API_URL = ((import.meta as any)?.env?.VITE_GIGA_API_URL as string | undefined)?.replace(/\/$/, '') || 'https://vm.getgiga.com';
export type CatalogCategory = {
  slug: string;
  name: string;
};
export type CatalogMethod = {
  key: string;
  display_name: string;
  description?: string | null;
  preferred?: boolean;
  priority?: number;
  setup_instructions?: string | null;
  doc_url?: string | null;
};
export type PageContent = {
  features?: {
    title: string;
    description: string;
    icon?: string;
  }[];
  example_prompts?: {
    prompt: string;
    result: string;
  }[];
  faqs?: {
    q: string;
    a: string;
  }[];
};
export type IntegrationListItem = {
  slug: string;
  display_name: string;
  logo_url: string | null;
  tagline: string | null;
  categories: CatalogCategory[];
  recommended_method: string | null;
};
export type IntegrationDetail = {
  slug: string;
  display_name: string;
  category?: string | null;
  categories: CatalogCategory[];
  description?: string | null;
  website_url?: string | null;
  doc_url?: string | null;
  logo_url: string | null;
  tagline: string | null;
  seo_title: string | null;
  seo_description: string | null;
  page_content: PageContent;
  recommended_method: string | null;
  methods: CatalogMethod[];
};
export async function fetchIntegrations(): Promise<IntegrationListItem[]> {
  const res = await fetch(`${GIGA_API_URL}/api/catalog/integrations?sort=popularity`);
  if (!res.ok) throw new Error(`Catalog request failed (${res.status})`);
  const data = await res.json();
  return (data.integrations ?? []) as IntegrationListItem[];
}
export async function fetchIntegration(slug: string): Promise<IntegrationDetail | null> {
  const res = await fetch(`${GIGA_API_URL}/api/catalog/integrations/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Integration request failed (${res.status})`);
  return (await res.json()) as IntegrationDetail;
}

// ── Build-time prime cache ──────────────────────────────────────────────────
// During the static prerender (Node, no browser) the data-driven pages can't
// wait on a client-side fetch/useEffect — effects don't run in renderToString.
// So the prerender fetches the catalog once and calls primeCatalog(), and the
// components seed their initial state synchronously from these getters. On the
// CLIENT the cache is empty, so components fall back to their normal useEffect
// fetch — behaviour is unchanged in the browser.
const _cache: {
  list?: IntegrationListItem[];
  details: Record<string, IntegrationDetail | null>;
} = {
  details: {}
};
export function primeCatalog(data: {
  list?: IntegrationListItem[];
  details?: Record<string, IntegrationDetail | null>;
}): void {
  if (data.list) _cache.list = data.list;
  if (data.details) Object.assign(_cache.details, data.details);
}
export function getCachedIntegrations(): IntegrationListItem[] | undefined {
  return _cache.list;
}

// Returns the cached detail, `null` if it's a known-missing slug, or `undefined`
// if the slug was never primed (→ client should fetch).
export function getCachedIntegration(slug: string): IntegrationDetail | null | undefined {
  return slug in _cache.details ? _cache.details[slug] : undefined;
}